import React, { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useTypedDispatch from '../../../../hooks/useTypedDispatch';
import useToast from '../../../../hooks/useToast';

import { setFlag, removeFlag } from '../../../../store/actions/Entity/Flag';
import type { PostType } from '../../../../types';

import './style.scss';

export interface FollowForumTopicProps {
  postId: number;
  postType: PostType
  value: boolean;
}

const FollowForumTopicButton: React.FC<FollowForumTopicProps> = ({
  postId,
  postType,
  value,
}) => {
  const dispatch = useTypedDispatch();
  const [latchedValue, setLatchedValue] = useState(value);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const { t } = useTranslation();

  const toggleFollow = async () => {
    setLoading(true);
    try {
      if (latchedValue) {
        await dispatch(removeFlag('follow_forum_topic', postType, postId));
        toast.show(
          <span>
            {t('pages.forum.follow_forum_topic.subject')}{' '}
            <strong>{t('pages.forum.follow_forum_topic.unfollowed')}</strong>
          </span>,
          'info',
        );
        setLatchedValue(false);
      } else {
        await dispatch(setFlag('follow_forum_topic', postType, postId));
        toast.show(
          <span>
            {t('pages.forum.follow_forum_topic.subject')}{' '}
            <strong>{t('pages.forum.follow_forum_topic.followed')}</strong>
          </span>,
          'success',
        );
        setLatchedValue(true);
      }
    } catch (error) {
      toast.show(
        <span>
          {t('pages.forum.follow_forum_topic.subject')}{' '}
          <strong>{t('pages.forum.follow_forum_topic.error')}</strong>
        </span>,
        'error',
      );
      console.error(error);
    }
    setLoading(false);
  };

  const classes = classNames('FollowForumButton', {
    'FollowForumButton--loading': loading,
    'FollowForumButton--latched': latchedValue,
  });

  const iconClasses = classNames('icon icon-bookmark icon-size-sm', {
    'icon-variant-2': !latchedValue,
    'icon-variant-7': latchedValue,
  });

  return (
    <div className={classes}>
      <button
        type="button"
        onClick={toggleFollow}
        aria-label={t('pages.forum.follow_forum_topic.button')}
        aria-pressed={latchedValue ? 'true' : 'false'}
      >
        <span className="FollowForumButton__label">{t('pages.forum.follow_forum_topic.follow')}</span>
        <i className={iconClasses} />
      </button>
    </div>
  );
};

export default FollowForumTopicButton;
